import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { TrialEngine, levelFromStreak } from "@/game/trials";
import { useAutoFocus } from "@/hooks/useAutoFocus";
import { A11yValue } from "@/components/A11yValue";

/**
 * Diagnostics — strumento di test del motore, separato dal flusso di gioco (Fase 2; a11y Fase 3).
 * Legge il motore in sola lettura: NON altera in alcun modo il gameplay.
 */
export default function Diagnostics() {
  const navigate = useNavigate();
  const headingRef = useAutoFocus();
  const [streak, setStreak] = useState(0);
  const [trial, setTrial] = useState(null);
  const [dist, setDist] = useState(null);

  const generateOne = () => {
    const engine = new TrialEngine();
    const t = engine.next(streak);
    setTrial(t);
  };

  const runBatch = () => {
    const engine = new TrialEngine();
    const counts = {};
    const N = 1000;
    for (let i = 0; i < N; i++) {
      const t = engine.next(streak);
      counts[t.id] = (counts[t.id] || 0) + 1;
    }
    setDist({ n: N, counts });
  };

  return (
    <main className="screen" data-testid="diagnostics-screen">
      <div className="screen__header">
        <button
          type="button"
          className="back-btn"
          data-testid="diagnostics-back-button"
          onClick={() => navigate("/")}
          aria-label="Torna alla schermata principale"
        >
          <ChevronLeft size={16} strokeWidth={2.5} aria-hidden="true" />
          Indietro
        </button>
        <h1 className="screen__title" tabIndex={-1} ref={headingRef}>
          Diagnostica
        </h1>
      </div>

      <div className="diag__controls">
        <label className="diag__field" htmlFor="diag-streak">
          Serie di risposte corrette
          <input
            id="diag-streak"
            type="number"
            min={0}
            className="diag__input"
            data-testid="diagnostics-streak-input"
            aria-label="Numero della serie di risposte corrette"
            value={streak}
            onChange={(e) => setStreak(Math.max(0, Number(e.target.value) || 0))}
          />
        </label>
        <A11yValue
          className="diag__field"
          visual={
            <>
              Livello: <b style={{ color: "var(--brand)", marginLeft: 4 }}>{levelFromStreak(streak)}</b>
            </>
          }
          spoken={`Livello di difficoltà ${levelFromStreak(streak)}`}
        />
        <button
          type="button"
          className="btn-outline"
          data-testid="diagnostics-generate-button"
          onClick={generateOne}
        >
          Genera prova
        </button>
        <button
          type="button"
          className="btn-outline"
          data-testid="diagnostics-batch-button"
          onClick={runBatch}
          aria-label="Genera mille prove e mostra la distribuzione"
        >
          Batch 1000
        </button>
      </div>

      {trial && (
        <div className="diag__card" data-testid="diagnostics-trial-card">
          <div className="diag__row">
            <span className="diag__key">identificativo</span>
            <span className="diag__val" data-testid="diagnostics-trial-id">{trial.id}</span>
          </div>
          <div className="diag__row">
            <span className="diag__key">categoria</span>
            <span className="diag__val">{trial.category}</span>
          </div>
          <div className="diag__row">
            <span className="diag__key">tocchi richiesti</span>
            <span className="diag__val diag__val--taps" data-testid="diagnostics-trial-taps">
              {trial.requiredTaps}
            </span>
          </div>
          <div className="diag__row">
            <span className="diag__key">testo mostrato</span>
            <span className="diag__val">{trial.prompt}</span>
          </div>
          <div className="diag__row">
            <span className="diag__key">annuncio vocale</span>
            <span className="diag__val">{trial.speech}</span>
          </div>
          {trial.explanation && (
            <div className="diag__row">
              <span className="diag__key">perché</span>
              <span className="diag__val">{trial.explanation}</span>
            </div>
          )}
        </div>
      )}

      {dist && (
        <div className="diag__dist" data-testid="diagnostics-distribution">
          <div>
            Distribuzione su <b>{dist.n}</b> prove (streak {streak}, livello {levelFromStreak(streak)}) —{" "}
            <b>{Object.keys(dist.counts).length}</b> tipi:
          </div>
          {Object.keys(dist.counts)
            .sort()
            .map((id) => (
              <div key={id}>
                {id.padEnd(20, "\u00A0")} <b>{dist.counts[id]}</b>
              </div>
            ))}
        </div>
      )}
    </main>
  );
}
